import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageComponent } from './ImageComponent';
import { ImageCarousel } from './ImageCarousel';
import { DeepFakeModal } from '../DeepFakeModal/DeepFakeModal';
import { ReactComponent as DeepFakeIcon } from '../../assets/deepfake.svg';
import Tooltip from '../Tooltip/Tooltip';
import styles from './ImagesContainer.module.scss';

export const ImagesContainer = ({ images, showCount, className, enableDeepFakeDetector = false }) => {
  const { t } = useTranslation();
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [deepFakeModal, setDeepFakeModal] = useState({ isOpen: false, imageUrl: '', selectedOption: 0 });
  const [imageClickCounts, setImageClickCounts] = useState({});
  const [detectingImages, setDetectingImages] = useState({});
  const [imageResults, setImageResults] = useState({});
  const [imageProbabilities, setImageProbabilities] = useState({});
  const dropdownRef = useRef(null);

  const handleImageClick = (index) => {
    setInitialIndex(index);
    setIsCarouselOpen(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDeepFakeClick = (imageUrl, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === imageUrl ? null : imageUrl);
  };

  const handleDropdownOptionClick = (imageUrl, optionValue, event) => {
    event.stopPropagation();
    setActiveDropdown(null);

    // Check if this image has been clicked before
    let selectedOption;
    if (imageClickCounts[imageUrl] !== undefined) {
      // If already clicked, use the stored option
      selectedOption = imageClickCounts[imageUrl];
    } else {
      // First click - store the selected option
      selectedOption = optionValue;

      // Store the selected option for this image
      setImageClickCounts((prev) => ({
        ...prev,
        [imageUrl]: selectedOption
      }));
    }

    // Set detecting state
    setDetectingImages((prev) => ({
      ...prev,
      [imageUrl]: true
    }));

    // Random delay between 1-4 seconds
    const delay = Math.floor(Math.random() * 3000) + 1000;

    setTimeout(() => {
      // Remove detecting state
      setDetectingImages((prev) => {
        const newState = { ...prev };
        delete newState[imageUrl];
        return newState;
      });

      // Even option (0, 2) = suspicious, odd option (1) = authentic
      const isSuspicious = selectedOption % 2 !== 1;
      const resultCode = isSuspicious ? 0 : 1;

      let probability;

      // Check if probability already exists for this image
      if (imageProbabilities[imageUrl] !== undefined) {
        probability = imageProbabilities[imageUrl];
      } else {
        // Generate new probability for first time
        if (isSuspicious) {
          // Suspicious: 95-99.9%
          probability = (Math.random() * 4.9 + 95).toFixed(1);
        } else {
          // Authentic: 0.1-1.0%
          probability = (Math.random() * 0.9 + 0.1).toFixed(1);
        }

        // Store the probability for this image
        setImageProbabilities((prev) => ({
          ...prev,
          [imageUrl]: probability
        }));
      }

      // Store the result (for icon color): 0 = authentic (green), 1 = suspicious (red)
      setImageResults((prev) => ({
        ...prev,
        [imageUrl]: resultCode
      }));

      // Open modal with the determined result
      setDeepFakeModal({
        isOpen: true,
        imageUrl: imageUrl,
        selectedOption: resultCode,
        probability: probability
      });
    }, delay);
  };

  const dropdownOptions = [
    { label: t('Reality Defender'), value: 0 },
    { label: t('Sensity.ai'), value: 1 },
    { label: t('DeepSafe'), value: 2 }
  ];

  return (
    <div className={className ? `${styles.imagesContainer} ${className}` : styles.imagesContainer}>
      {enableDeepFakeDetector && images?.slice(0, showCount).map((url, index) => {
        const isDetecting = detectingImages[url];
        const result = imageResults[url];
        const buttonClass = result !== undefined
          ? result === 1
            ? styles.deepFakeButtonGreen
            : styles.deepFakeButtonRed
          : styles.deepFakeButton;

        const hasResult = result !== undefined;
        const resultTooltip = hasResult
          ? result === 1
            ? t('Authentic')
            : t('Suspicious')
          : t('Detect DeepFake');

        return (
          <div key={index} className={styles.imageWithButton}>
            <ImageComponent url={url} onClick={() => handleImageClick(index)} />
            {isDetecting ? (
              <button
                className={styles.deepFakeButton}
                disabled={true}
              >
                <Tooltip content={t('Detecting...')} position="bottom">
                  <div className={styles.loader}></div>
                </Tooltip>
              </button>
            ) : (
              <button
                className={buttonClass}
                onClick={(e) => {
                  if (hasResult) {
                    // Reopen modal with stored results
                    e.stopPropagation();
                    setDeepFakeModal({
                      isOpen: true,
                      imageUrl: url,
                      selectedOption: imageClickCounts[url],
                      probability: imageProbabilities[url]
                    });
                  } else {
                    handleDeepFakeClick(url, e);
                  }
                }}
              >
                <Tooltip content={resultTooltip}>
                  <DeepFakeIcon />
                </Tooltip>
              </button>
            )}
            {activeDropdown === url && !isDetecting && !hasResult && (
              <div className={styles.dropdown} ref={dropdownRef}>
                {dropdownOptions.map((option) => (
                  <div
                    key={option.value}
                    className={styles.dropdownOption}
                    onClick={(e) => handleDropdownOptionClick(url, option.value, e)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {!enableDeepFakeDetector && images?.slice(0, showCount).map((url, index) => (
        <ImageComponent key={index} url={url} onClick={() => handleImageClick(index)} />
      ))}
      {images.length > showCount && (
        <div
          className={`${styles.moreImages} ${showCount === 1 ? styles.small : ''}`}
          onClick={() => handleImageClick(showCount)}
        >
          <div className={styles.moreImagesText}>+{images.length - showCount} images</div>
        </div>
      )}
      {isCarouselOpen ? <ImageCarousel
        images={images}
        initialIndex={initialIndex}
        isOpen={isCarouselOpen}
        onClose={(bool) => {
          setIsCarouselOpen(bool);
          setInitialIndex(0);
        }}
      /> : ''}
      {enableDeepFakeDetector ? <DeepFakeModal
          isOpen={deepFakeModal.isOpen}
          onClose={() => setDeepFakeModal({ isOpen: false, imageUrl: '', selectedOption: 0, probability: undefined })}
          imageUrl={deepFakeModal.imageUrl}
          selectedOption={deepFakeModal.selectedOption}
          probability={deepFakeModal.probability}
        /> : ''}
    </div>
  );
};

